import {HasMinMax} from '../components/forms/min-max-slider/min-max-slider.component';
import {UpdatedSince, ViewModes} from './enums';
import {SavedSearch} from './saved-search';
import LatLngLiteral = google.maps.LatLngLiteral;
import {SearchFiltersParams} from './search-filters-params';
import {SortBy} from './enums';
import MapOptions = google.maps.MapOptions;
import { BoundingBox } from './interfaces';

export class UserPreferences {

  // ################## WARNING WARNING ##################
  // User migration is storing profiles with 4.9, if we change this number, we must update the migration code or the migrated profiles will not work.
  // ################## WARNING WARNING ##################

  // TODO: do something where if we're using a saved profile from the DB, we have to compare the versions to check for changes that might break the code
  static latestVersionNumber: number = 5.4;  // Favourite Agent URL

  storedVersionNumber: number = UserPreferences.latestVersionNumber;

  positionInfo: PositionInfo = new PositionInfo();
  filters: Filters = new Filters();
  agentSearchFilters: SearchFiltersParams = new SearchFiltersParams();
  officeSearchFilters: SearchFiltersParams = new SearchFiltersParams();
  gallerySorting: SortBy = SortBy.NEWEST;

  locale: string = 'en';

  // Default the user to the Map mode.
  viewMode: ViewModes = ViewModes.MAP;

  // List of favourite listings
  favouriteListings: Array<string> = [];
  // We receive an array due to legacy RE/MAX, but we realistically only ever have one favourite agent
  favouriteAgents: Array<string> = [];
  favouriteAgentDetailUrl: string;


  moreInfoRequested: Array<string> = [];

  recentSearches: Array<string> = [];

  // TODO: Separate into stuff that should be saved to server and not

  // TODO: can we start grouping this>
  // List of Viewed listings
  viewedListings: Array<string> = [];

  // TODO: Separate into stuff we save into DB and stuff we don't
  savedSearches: Array<SavedSearch> = [];

  nextListings: {listingId: string, detailUrl: string}[] = [];

  constructor() {
    this.storedVersionNumber = UserPreferences.latestVersionNumber;
  }
  static containsFavouriteListings(favouriteListings: Array<string>, listingIds?: Array<string>): boolean {
    if (!listingIds) return false;
    return !!favouriteListings.find((value) => listingIds.indexOf(value) > -1);
  }

  static containsPreviouslyViewedListings(viewedListings: Array<string>, listingIds?: Array<string>): boolean {
    if (!listingIds) return false;

    let allFound = true;
    for (let i = 0; i < listingIds.length; i++) {
      if (viewedListings.indexOf(listingIds[i]) < 0) {
        allFound = false;
        break;
      }
    }

    return allFound;
  }
}

export class PositionInfo {
  north: number;
  south: number;
  east: number;
  west: number;
  zoomLevel: number = 5;
  mapOptions: MapOptions = {
    zoom: 15,
    zoomControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false
  };


  // Default center position for the map filter to be center of Canada
  centerLatLng: LatLngLiteral = {
    lat: 56.120483,
    lng: -95.226929
  };

  // Center position is used for moving the map to a position.
  // Physical location is kept separate to keep track of where you are, even if you're not currently displayed on the map.
  physicalLocation: LatLngLiteral;

  setProperties(_lat : number, _lng : number, listingDetails: BoundingBox, _physicalLocationLat: number, _physicalLocationLng: number){
    this.centerLatLng = {
      lat: _lat,
      lng: _lng
    };
    this.physicalLocation = {
      lat: _physicalLocationLat,
      lng: _physicalLocationLng
    }
    this.zoomLevel = this.mapOptions.zoom;
    this.north = listingDetails.north;
    this.south = listingDetails.south;
    this.east = listingDetails.east;
    this.west = listingDetails.west;
  }

  static getCenter(pi: PositionInfo): LatLngLiteral {
    if (!pi) return null;
    if (pi.centerLatLng && pi.centerLatLng.lat && pi.centerLatLng.lng) {
      return pi.centerLatLng;
    }
    if (pi.north && pi.south && pi.east && pi.west) {
      return new google.maps.LatLngBounds(
        { lat: pi.south, lng: pi.west },
        { lat: pi.north, lng: pi.east },
      ).getCenter().toJSON();
    }
    return null;
  }

  static clone(toCopy: PositionInfo): PositionInfo {
    const newPositionInfo = new PositionInfo();
    if (!toCopy) return newPositionInfo;
    newPositionInfo.north = toCopy.north;
    newPositionInfo.south = toCopy.south;
    newPositionInfo.west = toCopy.west;
    newPositionInfo.east = toCopy.east;
    newPositionInfo.zoomLevel = toCopy.zoomLevel;

    if (toCopy.centerLatLng) {
      newPositionInfo.centerLatLng = {
        lat: toCopy.centerLatLng.lat,
        lng: toCopy.centerLatLng.lng
      }
    }
    if (toCopy.physicalLocation) {
      newPositionInfo.physicalLocation = {
        lat: toCopy.physicalLocation.lat,
        lng: toCopy.physicalLocation.lng
      }
    }
    return newPositionInfo;
  }
}

export class Filters {
  price?: HasMinMax = { };
  pricePerSqFt?: HasMinMax = { };
  priceType?: number; // 0 = regular price, 1 = price per sqft
  size?: HasMinMax = { };
  beds?: HasMinMax = { };
  baths?: HasMinMax = { };
  featuredListings?: string;
  isRemaxListing?: boolean;
  minImages?: number;
  updatedSince: UpdatedSince; // 0 = Any, 1 = Yesterday, 2 = Last Week, 3 = Last Month
  // fields below are for commercial only
  totalAcres?: HasMinMax = { };
  parking?: HasMinMax = { };
  sqft?: HasMinMax = { };
  stories?: HasMinMax = { };
  units?: HasMinMax = { };
  businessTypes?: any;
  // end of commercial fields
  listingTypes ? = {
    house: false,
    condo: false,
    townhouse: false,
    recreational: false,
    farm: false,
    land: false,
    mobileHome: false,
    businessOpportunity: false,
    commercial: false,
    commercialLease: false,
    hotelResort: false
  };

  rentalsOnly?: boolean = false;
  hasOpenHouse?: boolean = false;

  customListingTypeIds?: Number[];

  static reset(filter: Filters) {
    filter.price = {};
    filter.size = {};
    filter.beds = {};
    filter.baths = {};
    filter.totalAcres = {};
    filter.parking = {};
    filter.sqft = {};
    filter.stories = {};
    filter.units = {};
    filter.businessTypes = {};
    filter.listingTypes = {
      house: false,
      condo: false,
      townhouse: false,
      recreational: false,
      farm: false,
      land: false,
      mobileHome: false,
      businessOpportunity: false,
      commercial: false,
      commercialLease: false,
      hotelResort: false
    };
    filter.rentalsOnly = false;
    filter.hasOpenHouse = false;
    return filter;
  }

  static clone(toCopy: Filters) {
    const newFilter: Filters = new Filters();
    if (!toCopy) return newFilter;

    Object.keys(toCopy).forEach((k) => {
      if (k === 'price' || k === 'size' || k === 'beds' || k === 'baths') {
        if (toCopy[k] && toCopy[k].min) newFilter[k].min = toCopy[k].min;
        if (toCopy[k] && toCopy[k].max) newFilter[k].max = toCopy[k].max;
      }
      else if (k === 'listingTypes') {
        Object.keys(toCopy.listingTypes).forEach((t) => {
          newFilter.listingTypes[t] = toCopy.listingTypes[t];
        });
      }
      else newFilter[k] = toCopy[k];
    });
    return newFilter;
  }
}
